import React from "react";
import { Fibonacci, FiboType } from "../types";

import {
  BigLabel,
  ExportableComponent,
  exportLabelComponent,
  FiboFourWay,
  FiboTwoWay,
  getRenderedComponent,
  LabelProps,
  SmallLabel,
} from "./Preview";
import blobStream from "blob-stream";
import FileSaver from "file-saver";
import fs from "fs";
import JSZip from "jszip";
import { finishes } from "./FiboDetails";
import { RootState } from "./store";

const DEVICES_PER_ROW = 3;
const SIZE_ON_PAGE = 350;

async function writePdf(
  document: RootState["document"],
  pdfDoc: PDFKit.PDFDocument
) {
  function fiboInfoString(index: number, fibo: Fibonacci) {
    const seqNumString = (index + 1).toString().padStart(2, "0");

    return `${seqNumString} - ${fibo.amount} x 027${
      (fibo.type + 1) * 2
    }-${fibo.finish.toString().padStart(2, "0")}\n${
      fibo.type === FiboType.Quad ? "Quad" : "Dual"
    } ${finishes.find((f) => f.id === fibo.finish)?.text}`.toLowerCase();
  }

  const fibonaccis = Object.values(document.fibonaccis.entities).filter(
    (fibo) => !!fibo
  ) as Fibonacci[];

  const chunkedFibonaccis: Fibonacci[][] = [];
  let index = 0;
  while (index < fibonaccis.length) {
    chunkedFibonaccis.push(fibonaccis.slice(index, DEVICES_PER_ROW + index));
    index += DEVICES_PER_ROW;
  }

  for (let row = 0; row < chunkedFibonaccis.length; row++) {
    pdfDoc.addPage();

    const fibonaccis = chunkedFibonaccis[row];
    for (let column = 0; column < fibonaccis.length; column++) {
      const fibonacci = fibonaccis[column];

      const xMargin = 20;
      const xPos =
        xMargin +
        (pdfDoc.page.width - 2 * xMargin - fibonaccis.length * SIZE_ON_PAGE) /
          fibonaccis.length /
          2 +
        ((pdfDoc.page.width - 2 * xMargin) / fibonaccis.length) * column;

      const yPos = pdfDoc.page.height / 2 - (SIZE_ON_PAGE / 2 + 30);

      const ReactType =
        fibonacci.type === FiboType.Dual ? FiboTwoWay : FiboFourWay;
      // Draw Fibonacci
      (await import("svg-to-pdfkit")).default(
        pdfDoc,
        `<svg viewBox="0 0 80 120">
          ${await getRenderedComponent(
            <ReactType
              id={fibonacci.id}
              label={fibonacci.label as any}
              finish={fibonacci.finish}
              textOffsetFromIcon={document.advanced.textOffsetFromIcon}
              fontSize={document.advanced.fontSize}
              iconSize={document.advanced.iconSize}
              strokeWidth={document.advanced.strokeWidth}
              exportMode={true}
            />
          )}</svg>`,
        xPos,
        yPos,
        {
          width: SIZE_ON_PAGE,
          height: SIZE_ON_PAGE,
        }
      );

      // Draw description
      pdfDoc.fontSize(10).font("Helvetica-Bold");
      const descOptions = {
        align: "center",
        width: SIZE_ON_PAGE,
      };
      const descHeight = pdfDoc.heightOfString(
        fibonacci.description,
        descOptions
      );
      pdfDoc.text(
        fibonacci.description,
        xPos,
        yPos - descHeight - 8,
        descOptions
      );

      // Draw product info
      pdfDoc.fontSize(10).font("Helvetica");
      const infoOpts = {
        align: "center",
        width: SIZE_ON_PAGE,
      };
      pdfDoc.text(
        fiboInfoString(row * 3 + column, fibonacci),
        xPos,
        yPos + SIZE_ON_PAGE + 15,
        infoOpts
      );
    }
  }

  // We're done drawing the Fibos 😅
  // Draw some logo's and references and stuff
  const pageRange = pdfDoc.bufferedPageRange();
  for (
    let page = pageRange.start;
    page < pageRange.start + pageRange.count;
    page++
  ) {
    pdfDoc.switchToPage(page);

    // Page numbers
    const pageNumber = `${page + 1}/${pageRange.count}`;
    const width = pdfDoc.widthOfString(pageNumber);
    pdfDoc.text(pageNumber, pdfDoc.page.width - width - 10, 10, {
      align: "right",
      width,
    });

    // References and stuff
    pdfDoc.fontSize(12);
    let i = 0;
    const labelLocations = [
      [45, pdfDoc.page.height - 60],
      [45, pdfDoc.page.height - 40],
      [330, pdfDoc.page.height - 60],
    ];
    const valueLocations = [
      [170, pdfDoc.page.height - 60],
      [170, pdfDoc.page.height - 40],
      [455, pdfDoc.page.height - 60],
    ];

    [
      ["Customer name:", document.metadata.customerName],
      ["Order reference:", document.metadata.orderRef],
      ["basalte.world order:", document.metadata.worldOrder],
    ].forEach(([label, value]) => {
      if (value) {
        pdfDoc.text(label, labelLocations[i][0], labelLocations[i][1]);
        pdfDoc.text(value, valueLocations[i][0], valueLocations[i][1]);
        i++;
      }
    });

    // Title
    pdfDoc.fontSize(13).font("Helvetica-Bold");
    pdfDoc.text(
      `Fibonacci Custom Labels - ${
        document.metadata.title || "Untitled Project"
      }`,
      20,
      20
    );

    // Basalte Logo
    (await import("svg-to-pdfkit")).default(
      pdfDoc,
      (await import("!!raw-loader!../svg/basalte.svg")).default,
      pdfDoc.page.width - 150,
      pdfDoc.page.height - 55,
      {
        width: 100,
        preserveAspectRatio: "xMinYMin",
      }
    );

    pdfDoc.fontSize(9).font("Helvetica");
    pdfDoc.fillColor([50, 50, 50]);
    pdfDoc.text(
      "www.basalte.be",
      pdfDoc.page.width - 200,
      pdfDoc.page.height - 25,
      { align: "center" }
    );
  }
}

export async function generatePdf(document: RootState["document"]) {
  fs.writeFileSync(
    "data/Helvetica.afm",
    (await import("!!raw-loader!pdfkit/js/data/Helvetica.afm")).default
  );
  fs.writeFileSync(
    "data/Helvetica-Bold.afm",
    (await import("!!raw-loader!pdfkit/js/data/Helvetica-Bold.afm")).default
  );
  const pdfDoc = new (await import("pdfkit")).default({
    layout: "landscape",
    size: "a4",
    autoFirstPage: false,
    bufferPages: true,
    margin: 0, // We'll figure this out on our own
    info: {
      Title: `Fibonacci Custom Labels - [${
        document.metadata.title || "Untitled Project"
      }]`,
      Author: "basalte",
      json: JSON.stringify(document),
    } as any,
  });
  const stream = pdfDoc.pipe(blobStream());
  await writePdf(document, pdfDoc);
  pdfDoc.end();

  return new Promise<Blob>((resolve, reject) => {
    stream.on("finish", () => {
      const blob = stream.toBlob("application/pdf");
      resolve(blob);
    });

    // TODO: error
  });
}

export async function downloadPdf(document: RootState["document"]) {
  generatePdf(document).then((blob) => {
    FileSaver.saveAs(
      blob,
      `Fibonacci Custom Labels - [${
        document.metadata.title || "Untitled Project"
      }].pdf`
    );
  });
}

export async function laserExport(document: RootState["document"]) {
  const zipFile = new JSZip();

  const promises = Object.values(document.fibonaccis.entities).map(
    async (fibo, i) => {
      if (!fibo) return Promise.resolve();

      const exportLabel = (
        labelIndex: number,
        Component: React.FunctionComponent<LabelProps>,
        filename: string
      ) => {
        const label = fibo.label[labelIndex];

        return exportLabelComponent(
          <ExportableComponent
            id={fibo.id}
            labelIndex={labelIndex}
            showPopOver={false}
            x={0}
            y={0}
            width={Component === BigLabel ? 47 : 31}
            height={7}
            laserExport={true}
          >
            <Component
              label={label}
              fontSize={document.advanced.fontSize}
              iconSize={document.advanced.iconSize}
              strokeWidth={document.advanced.strokeWidth}
              textOffsetFromIcon={document.advanced.textOffsetFromIcon}
            />
          </ExportableComponent>
        ).then((blob) => {
          zipFile.file(`${filename}-laser.pdf`, blob, { binary: true });
        });
      };

      const filename = `${(i + 1).toString().padStart(3, "0")} - ${
        fibo.description
      }`;
      if (fibo.type === FiboType.Quad) {
        return await Promise.all([
          exportLabel(0, BigLabel, `${filename} - Big`),
          exportLabel(1, SmallLabel, `${filename} - Small`),
        ]);
      } else {
        return exportLabel(0, BigLabel, filename);
      }
    }
  );

  await Promise.all(promises);

  zipFile.generateAsync({ type: "blob" }).then((blob) => {
    FileSaver.saveAs(
      blob,
      `${document.metadata.title || "untitled"}-laser.zip`
    );
  });
}

export async function packingLabelExport(fiboDocument: RootState["document"]) {
  const zipFile = new JSZip();
  const csvWriter = await import("csv-writer");
  const mappingCsvWriter = csvWriter.createObjectCsvStringifier({
    header: [
      { id: "title", title: "PROJECTNAME" },
      { id: "customerName", title: "CUSTOMER" },
      { id: "orderRef", title: "ORDER_REF" },
      { id: "worldOrder", title: "WORLD_ORDER" },
      { id: "id", title: "ID" },
      { id: "description", title: "DESCRIPTION" },
      { id: "type", title: "TYPE" },
      { id: "artN", title: "ART N" },
      { id: "amount", title: "AMOUNT" },
      { id: "finish", title: "FINISH" },
      { id: "image", title: "IMAGE" },
    ],
  });
  const csvFibos: {
    id: string;
    description: string;
    type: string;
    artN: string;
    amount: number;
    finish: string;
    image: string;
  }[] = [];

  const canvas = document.createElement("canvas");
  const imWidth = 800;
  const imHeight = 1200;
  const promises = Object.values(fiboDocument.fibonaccis.entities).map(
    async (fibo, i) => {
      if (!fibo) return Promise.resolve();

      csvFibos.push({
        ...fiboDocument.metadata,
        id: fibo.id,
        amount: fibo.amount,
        description: fibo.description,
        finish: finishes.find((f) => f.id === fibo.finish)?.text ?? "",
        type: fibo.type === FiboType.Quad ? "Quad" : "Dual",
        artN: fibo.type === FiboType.Quad ? "0274-00" : "0272-00",
        image: `${i}.png`,
      });

      const ReactType = fibo.type === FiboType.Dual ? FiboTwoWay : FiboFourWay;
      const svg = `<svg viewBox="-5 -5 90 130" xmlns="http://www.w3.org/2000/svg" width="${imWidth}" height="${imHeight}">
        ${await getRenderedComponent(
          <ReactType
            id={fibo.id}
            label={fibo.label as any}
            finish={fibo.finish}
            textOffsetFromIcon={fiboDocument.advanced.textOffsetFromIcon}
            fontSize={fiboDocument.advanced.fontSize}
            iconSize={fiboDocument.advanced.iconSize}
            strokeWidth={fiboDocument.advanced.strokeWidth}
            exportMode={true}
          />
        )}</svg>`;

      const context = canvas.getContext("2d");
      if (!context) throw new Error("Could not initialize context");

      return new Promise<void>((resolve, reject) => {
        const loader = new Image();
        loader.onload = () => {
          loader.width = canvas.width = imWidth;
          loader.height = canvas.height = imHeight;
          context.drawImage(loader, 0, 0, loader.width, loader.height);
          canvas.toBlob((blob) => {
            if (blob) {
              zipFile.file(`${i}.png`, blob, { binary: true });
              return resolve();
            }
            reject();
          });
        };
        loader.src = "data:image/svg+xml," + encodeURIComponent(svg);
      });
    }
  );

  await Promise.all(promises);

  const mappingString =
    mappingCsvWriter.getHeaderString() +
    mappingCsvWriter.stringifyRecords(csvFibos);
  zipFile.file(`mapping.csv`, mappingString);

  zipFile.generateAsync({ type: "blob" }).then((blob) => {
    FileSaver.saveAs(
      blob,
      `${fiboDocument.metadata.title || "untitled"}-labels.zip`
    );
  });
}
